import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { isoDate } from '../../domain/calendar.js';
import type { AisleCategory, Store, Unit, WeekPlan } from '../../domain/types.js';
import { DEFAULT_SETTINGS, type Settings } from './settings.js';
import { EMPTY_LIBRARY, type Library } from './library.js';
import type { ItemMeta } from '../sync/merge.js';
import { deviceId } from '../sync/device.js';
import {
  cookKey, dinnerKey, favKey, libStapleKey, lunchKey, manualKey, pantryKey, recipeKey, stapleKey, tickKey,
} from '../sync/keys.js';

/**
 * Storage holds PLAIN strings; the engine takes BRANDED IsoDate. `toWeekPlan`
 * is the only crossing, and it re-validates every date on the way through — so
 * a corrupted date throws at load rather than quietly producing a wrong list.
 */
export interface StoredDinner {
  date: string;
  mealId: string;
  spansDays: number;
  portions: number | null;
}

export interface StoredLunch {
  date: string;
  personId: string;
  componentIds: string[];
}

export interface StoredStaple {
  ingredientId: string;
  qty: number;
  unit: Unit;
}

/** Something no meal plan can predict: bin bags, nappies, wine. */
export interface ManualItem {
  id: string;
  label: string;
  aisle: AisleCategory;
}

export interface StoredPlan {
  weekStart: string;
  store: Store;
  dinners: StoredDinner[];
  lunches: StoredLunch[];
  staples: StoredStaple[];
  pantry: Record<string, boolean>;
  /** Shopping-list tick state, keyed by ingredientId or manual item id. */
  ticked: Record<string, boolean>;
  manual: ManualItem[];
  updatedAt: string;
  /**
   * Per-item sync metadata, keyed by the derived keys in `sync/keys.ts`.
   *
   * Deliberately a SIBLING of the arrays above rather than woven into them:
   * every existing field keeps its exact shape, so `toWeekPlan` and the whole
   * domain engine never see this and none of their tests change.
   *
   * An entry with `deleted: true` is a tombstone whose array entry is gone.
   * That is what stops the other phone's older copy silently resurrecting
   * something this one deleted.
   *
   * Optional because a plan written before this phase does not have it. Every
   * read goes through `withItemMeta`, which backfills, so code downstream of
   * `loadPlan`/`listPlans` can rely on it being present.
   */
  itemMeta?: Record<string, ItemMeta>;
}

/** Per-week sync bookkeeping. Absent until a week has synced at least once. */
export interface SyncState {
  weekStart: string;
  lastSyncedAt: string | null;
  /**
   * Whether this device has answered the first-run question for this week.
   * Asked once per week per device, never assumed — no default path may
   * discard a plan someone has already made.
   */
  adopted?: boolean;
}

interface EdenDB extends DBSchema {
  plans: { key: string; value: StoredPlan };
  settings: { key: string; value: Settings };
  library: { key: string; value: Library };
  sync: { key: string; value: SyncState };
}

const SETTINGS_KEY = 'settings';
const LIBRARY_KEY = 'library';

let dbPromise: Promise<IDBPDatabase<EdenDB>> | null = null;

function db(): Promise<IDBPDatabase<EdenDB>> {
  dbPromise ??= openDB<EdenDB>('eden-eats', 2, {
    upgrade(database) {
      // Guarded so a later version bump does not throw on an existing database.
      if (!database.objectStoreNames.contains('plans')) {
        database.createObjectStore('plans', { keyPath: 'weekStart' });
      }
      if (!database.objectStoreNames.contains('settings')) {
        database.createObjectStore('settings');
      }
      if (!database.objectStoreNames.contains('library')) {
        database.createObjectStore('library');
      }
      // v2. Backfilling `itemMeta` is NOT done here: rewriting every stored
      // plan inside the upgrade transaction risks the transaction closing
      // mid-migration and leaving half the weeks converted. It happens lazily
      // on read instead (`withItemMeta`), which is idempotent and cannot half
      // succeed.
      if (!database.objectStoreNames.contains('sync')) {
        database.createObjectStore('sync', { keyPath: 'weekStart' });
      }
    },
  }).catch((error: unknown) => {
    // A rejected promise is neither null nor undefined, so `??=` would never
    // retry. One failed open — private browsing, exhausted quota, storage
    // blocked — would otherwise wedge the app's only memory for the whole
    // session, with a plan possibly saved and then unreadable.
    dbPromise = null;
    throw error;
  });
  return dbPromise;
}

export function emptyPlan(weekStart: string, store: Store): StoredPlan {
  return {
    weekStart,
    store,
    dinners: [],
    lunches: [],
    staples: [],
    pantry: {},
    ticked: {},
    manual: [],
    updatedAt: new Date().toISOString(),
    itemMeta: {},
  };
}

/**
 * The v1 -> v2 backfill, applied on read.
 *
 * A plan written before this phase has no `itemMeta`. Every item in it is
 * stamped with the plan's own `updatedAt` and this device — both of which are
 * honest: that timestamp genuinely is the last moment anything in the plan was
 * touched, and before sharing existed every item on a device was made on that
 * device.
 *
 * Idempotent by construction — an existing entry is never overwritten — so a
 * partially-migrated plan (or one merged from a device still on v1) converges
 * rather than losing the timestamps it already had.
 */
export function withItemMeta(plan: StoredPlan): StoredPlan {
  const at = plan.updatedAt;
  const by = deviceId();
  const meta: Record<string, ItemMeta> = { ...(plan.itemMeta ?? {}) };
  let added = false;
  const stamp = (key: string): void => {
    if (meta[key]) return;
    meta[key] = { updatedAt: at, updatedBy: by };
    added = true;
  };

  for (const d of plan.dinners) stamp(dinnerKey(d.date));
  for (const l of plan.lunches) stamp(lunchKey(l.date, l.personId));
  for (const s of plan.staples) stamp(stapleKey(s.ingredientId));
  for (const m of plan.manual) stamp(manualKey(m.id));
  for (const k of Object.keys(plan.pantry)) stamp(pantryKey(k));
  for (const k of Object.keys(plan.ticked)) stamp(tickKey(k));

  if (!added && plan.itemMeta) return plan;
  return { ...plan, itemMeta: meta };
}

/** The same backfill for the library, which outlives any single week. */
export function withLibraryMeta(library: Library): Library {
  const at = library.updatedAt ?? new Date().toISOString();
  const by = deviceId();
  const meta: Record<string, ItemMeta> = { ...(library.itemMeta ?? {}) };
  let added = false;
  const stamp = (key: string): void => {
    if (meta[key]) return;
    meta[key] = { updatedAt: at, updatedBy: by };
    added = true;
  };

  for (const mealId of library.favourites) stamp(favKey(mealId));
  for (const e of library.cookLog) stamp(cookKey(e.mealId, e.cookedOn));
  for (const s of library.staples) stamp(libStapleKey(s.ingredientId));

  // A library saved before custom recipes existed has no `customMeals` field.
  const stored = (library as Partial<Library>).customMeals;
  const customMeals = stored ?? [];
  for (const m of customMeals) stamp(recipeKey(m.id));

  if (!added && library.itemMeta && library.updatedAt && stored) return library;
  return { ...library, customMeals, itemMeta: meta, updatedAt: at };
}

export async function loadPlan(weekStart: string): Promise<StoredPlan | undefined> {
  const stored = await (await db()).get('plans', weekStart);
  return stored ? withItemMeta(stored) : undefined;
}

export async function savePlan(plan: StoredPlan): Promise<void> {
  await (await db()).put('plans', { ...plan, updatedAt: new Date().toISOString() });
}

export async function listPlans(): Promise<StoredPlan[]> {
  return (await (await db()).getAll('plans')).map(withItemMeta);
}

export async function loadSyncState(weekStart: string): Promise<SyncState> {
  return (await (await db()).get('sync', weekStart)) ?? { weekStart, lastSyncedAt: null };
}

export async function saveSyncState(state: SyncState): Promise<void> {
  await (await db()).put('sync', state);
}

/** The single crossing from storage into the engine. */
export function toWeekPlan(stored: StoredPlan): WeekPlan {
  return {
    weekStart: isoDate(stored.weekStart),
    store: stored.store,
    dinners: stored.dinners.map((d) => ({
      date: isoDate(d.date),
      mealId: d.mealId,
      spansDays: d.spansDays,
      portions: d.portions,
    })),
    lunches: stored.lunches.map((l) => ({
      date: isoDate(l.date),
      personId: l.personId,
      // Copied, not shared. A lunch editor that mutates this array in place
      // would otherwise corrupt the stored plan without ever calling savePlan.
      componentIds: [...l.componentIds],
    })),
    staples: stored.staples.map((s) => ({ ...s })),
    pantry: { ...stored.pantry },
  };
}

export async function loadSettings(): Promise<Settings> {
  return (await (await db()).get('settings', SETTINGS_KEY)) ?? DEFAULT_SETTINGS;
}

export async function saveSettings(settings: Settings): Promise<void> {
  await (await db()).put('settings', settings, SETTINGS_KEY);
}

export async function loadLibrary(): Promise<Library> {
  return withLibraryMeta((await (await db()).get('library', LIBRARY_KEY)) ?? EMPTY_LIBRARY);
}

export async function saveLibrary(library: Library): Promise<void> {
  await (await db()).put('library', library, LIBRARY_KEY);
}

/**
 * Test-only. Every app test file shares ONE database name ('eden-eats')
 * across every test it contains, and several tests carry no reset at all
 * between them — correctness has been resting entirely on declaration
 * order matching execution order.
 *
 * Clearing the object stores, not deleting-and-reopening the database, is
 * deliberate: `indexedDB.deleteDatabase` has to wait for every OTHER open
 * connection on this name to close first, and a test that calls
 * `vi.resetModules()` (see planStore.test.tsx's "gives manual items distinct
 * ids, even across a reload") leaves exactly such a connection behind —
 * orphaned from a module instance nothing can reach any more, so it never
 * closes and the delete request hangs forever. A `clear()` transaction needs
 * no such exclusivity, so it can't deadlock on a leftover connection.
 */
export async function resetDbForTests(): Promise<void> {
  const database = await db();
  await Promise.all([
    database.clear('plans'),
    database.clear('settings'),
    database.clear('library'),
    database.clear('sync'),
  ]);
}
